import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Upload, Info, X, ImagePlus, ChevronLeft, ChevronRight, Video } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useChecklistById } from "@/hooks/useChecklists";
import { usePropertyRooms } from "@/hooks/usePropertyRooms";
import { useImportTurnoverPhotos, type ImportUploadProgress } from "@/hooks/useImportTurnoverPhotos";
import type { Room } from "@/types/checklist";
import { getLocalizedRoomName } from "@/types/checklist";
import { cn } from "@/lib/utils";
import { Progress } from "@/components/ui/progress";
import {
  clearImportTurnoverDraft,
  getImportTurnoverDraft,
  saveImportTurnoverDraft,
  type CachedStagedMedia,
  type ImportWizardStep,
} from "@/lib/importTurnoverDraftCache";

interface ImportTurnoverPhotosDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyId: string;
  taskId: string;
  checklistId?: string | null;
  existingSubmissionId?: string | null;
  taskStatus: string;
  onSuccess?: () => void;
}

const ACCEPT_MEDIA = "image/*,video/*,.heic,.HEIC";
const STEPS: ImportWizardStep[] = ["before", "after", "review"];

function isVideoFile(file: File): boolean {
  return (file.type || "").startsWith("video/");
}

function createStagedMedia(file: File, phase: "before" | "after"): CachedStagedMedia {
  return {
    id: crypto.randomUUID(),
    file,
    previewUrl: URL.createObjectURL(file),
    phase,
    roomName: "",
    isIssue: false,
    issueDescription: "",
  };
}

function MediaPreview({ item, className }: { item: CachedStagedMedia; className?: string }) {
  if (isVideoFile(item.file)) {
    return (
      <div className={cn("relative overflow-hidden rounded-md border bg-black", className)}>
        <video src={item.previewUrl} className="h-full w-full object-cover" muted playsInline />
        <div className="absolute bottom-1 right-1 rounded bg-black/60 p-0.5">
          <Video className="h-3 w-3 text-white" />
        </div>
      </div>
    );
  }
  return (
    <img src={item.previewUrl} alt="" className={cn("rounded-md border object-cover", className)} />
  );
}

function stepLabel(step: ImportWizardStep): string {
  if (step === "before") return "Before photos";
  if (step === "after") return "After photos";
  return "Review";
}

function validatePhaseItems(_items: CachedStagedMedia[]): string | null {
  return null;
}

export function ImportTurnoverPhotosDialog({
  open,
  onOpenChange,
  propertyId,
  taskId,
  checklistId,
  existingSubmissionId,
  taskStatus,
  onSuccess,
}: ImportTurnoverPhotosDialogProps) {
  const { data: checklist } = useChecklistById(open ? checklistId ?? undefined : undefined);
  const { data: propertyRooms = [] } = usePropertyRooms(open ? propertyId : undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadPhaseRef = useRef<"before" | "after">("before");
  const initializedRef = useRef(false);

  const [step, setStep] = useState<ImportWizardStep>("before");
  const [beforeItems, setBeforeItems] = useState<CachedStagedMedia[]>([]);
  const [afterItems, setAfterItems] = useState<CachedStagedMedia[]>([]);
  const [extraRooms, setExtraRooms] = useState<string[]>([]);
  const [notes, setNotes] = useState("Turnover photos imported by host.");
  const [markTaskCompleted, setMarkTaskCompleted] = useState(taskStatus !== "completed");
  const [customRoom, setCustomRoom] = useState("");
  const [bulkRoom, setBulkRoom] = useState("");
  const [uploadProgress, setUploadProgress] = useState<ImportUploadProgress | null>(null);

  const importPhotos = useImportTurnoverPhotos();

  const checklistRooms = useMemo(() => {
    const raw = checklist?.rooms;
    if (!Array.isArray(raw)) return [] as Room[];
    return raw as Room[];
  }, [checklist?.rooms]);

  const roomOptions = useMemo(() => {
    const names = checklistRooms.length > 0
      ? checklistRooms.map((r) => getLocalizedRoomName(r))
      : propertyRooms.map((r) => r.name);
    return [...new Set([...names.map((n) => n.trim()).filter(Boolean), ...extraRooms])];
  }, [checklistRooms, propertyRooms, extraRooms]);

  const persistDraft = useCallback(() => {
    saveImportTurnoverDraft(taskId, {
      step,
      beforeItems,
      afterItems,
      notes,
      markTaskCompleted,
      extraRooms,
    });
  }, [taskId, step, beforeItems, afterItems, notes, markTaskCompleted, extraRooms]);

  useEffect(() => {
    if (!open) {
      initializedRef.current = false;
      return;
    }
    if (initializedRef.current) return;
    initializedRef.current = true;

    const cached = getImportTurnoverDraft(taskId);
    if (cached) {
      setStep(cached.step);
      setBeforeItems(cached.beforeItems);
      setAfterItems(cached.afterItems);
      setNotes(cached.notes);
      setMarkTaskCompleted(cached.markTaskCompleted);
      setExtraRooms(cached.extraRooms);
      return;
    }

    setStep("before");
    setBeforeItems([]);
    setAfterItems([]);
    setExtraRooms([]);
    setCustomRoom("");
    setBulkRoom("");
    setMarkTaskCompleted(taskStatus !== "completed");
    setNotes("Turnover photos imported by host.");
  }, [open, taskId, taskStatus]);

  useEffect(() => {
    if (!open) return;
    persistDraft();
  }, [open, persistDraft]);

  const addFiles = (files: FileList | null, phase: "before" | "after") => {
    if (!files?.length) return;
    const next = Array.from(files).map((f) => createStagedMedia(f, phase));
    if (phase === "before") setBeforeItems((prev) => [...prev, ...next]);
    else setAfterItems((prev) => [...prev, ...next]);
  };

  const removeItem = (id: string, phase: "before" | "after") => {
    const setter = phase === "before" ? setBeforeItems : setAfterItems;
    setter((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  };

  const updateItem = (id: string, phase: "before" | "after", patch: Partial<CachedStagedMedia>) => {
    const setter = phase === "before" ? setBeforeItems : setAfterItems;
    setter((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  };

  const applyBulkRoom = (phase: "before" | "after", onlyUnassigned: boolean) => {
    const room = bulkRoom.trim();
    if (!room) {
      toast({ title: "Pick a room", description: "Choose a room for bulk assign.", variant: "destructive" });
      return;
    }
    const setter = phase === "before" ? setBeforeItems : setAfterItems;
    setter((prev) =>
      prev.map((p) => (!onlyUnassigned || !p.roomName.trim() ? { ...p, roomName: room } : p)),
    );
  };

  const totalCount = beforeItems.length + afterItems.length;
  const beforeError = validatePhaseItems(beforeItems);
  const afterError = validatePhaseItems(afterItems);
  const canSubmit = totalCount > 0 && !beforeError && !afterError;

  const goNext = () => {
    if (step === "before") {
      if (beforeError) {
        toast({ title: "Finish before photos", description: beforeError, variant: "destructive" });
        return;
      }
      setStep("after");
      return;
    }
    if (step === "after") {
      if (afterError) {
        toast({ title: "Finish after photos", description: afterError, variant: "destructive" });
        return;
      }
      setStep("review");
    }
  };

  const goBack = () => {
    if (step === "after") setStep("before");
    else if (step === "review") setStep("after");
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast({
        title: "Add photos to import",
        description: "Upload at least one before or after photo, or finish flagged issue descriptions.",
        variant: "destructive",
      });
      return;
    }

    const assignedPhotos = [...beforeItems, ...afterItems].map((p) => ({
      file: p.file,
      roomName: p.roomName,
      phase: p.phase,
      isIssue: p.isIssue,
      issueDescription: p.isIssue ? p.issueDescription : undefined,
    }));

    try {
      setUploadProgress({ completed: 0, total: assignedPhotos.length, phase: "uploading" });
      const result = await importPhotos.mutateAsync({
        propertyId,
        taskId,
        checklistId,
        checklistRooms,
        existingSubmissionId,
        assignedPhotos,
        notes,
        markTaskCompleted,
        onProgress: setUploadProgress,
      });
      setUploadProgress(null);
      clearImportTurnoverDraft(taskId);
      toast({
        title: "Photos imported",
        description: `${result.photoCount} file${result.photoCount !== 1 ? "s" : ""} added${
          result.issueCount > 0 ? ` (${result.issueCount} issue${result.issueCount !== 1 ? "s" : ""})` : ""
        }.`,
      });
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      setUploadProgress(null);
      toast({
        title: "Import failed",
        description: err instanceof Error ? err.message : "Could not import photos.",
        variant: "destructive",
      });
    }
  };

  const renderPhasePanel = (phase: "before" | "after") => {
    const items = phase === "before" ? beforeItems : afterItems;
    const phaseTitle = phase === "before" ? "Before" : "After";
    const unassigned = items.filter((p) => !p.roomName.trim()).length;

    return (
      <div className="space-y-4">
        <Alert className="bg-muted/30">
          <Info className="h-4 w-4" />
          <AlertDescription>
            All files uploaded here are marked as <strong>{phaseTitle.toLowerCase()}</strong>.
            Room assignment is optional — use bulk assign if you want photos grouped by room on the
            report.
          </AlertDescription>
        </Alert>

        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT_MEDIA}
          multiple
          className="hidden"
          onChange={(e) => {
            addFiles(e.target.files, uploadPhaseRef.current);
            e.target.value = "";
          }}
        />

        <button
          type="button"
          onClick={() => {
            uploadPhaseRef.current = phase;
            fileInputRef.current?.click();
          }}
          className="flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-muted-foreground/30 bg-muted/20 px-6 py-8 transition-colors hover:border-primary/50 hover:bg-muted/40"
        >
          <ImagePlus className="h-9 w-9 text-muted-foreground" />
          <span className="text-sm font-medium">Add {phaseTitle.toLowerCase()} photos & videos</span>
          <span className="text-xs text-muted-foreground">Select many at once from your device</span>
        </button>

        {items.length > 0 && (
          <>
            <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/20 p-2">
              <Select value={bulkRoom || undefined} onValueChange={setBulkRoom}>
                <SelectTrigger className="h-8 w-[140px] text-xs">
                  <SelectValue placeholder="Bulk room" />
                </SelectTrigger>
                <SelectContent>
                  {roomOptions.map((name) => (
                    <SelectItem key={name} value={name}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="button" variant="secondary" size="sm" className="h-8 text-xs" onClick={() => applyBulkRoom(phase, true)}>
                Apply to unassigned
              </Button>
              <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={() => applyBulkRoom(phase, false)}>
                Apply to all
              </Button>
              <span className="ml-auto text-xs text-muted-foreground">
                {items.length} file{items.length !== 1 ? "s" : ""}
                {unassigned > 0 && <span className="text-amber-600"> · {unassigned} unassigned</span>}
              </span>
            </div>

            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((item) => {
                const needsIssueDesc = item.isIssue && !item.issueDescription.trim();
                return (
                  <div
                    key={item.id}
                    className={cn(
                      "space-y-2 rounded-lg border p-2",
                      needsIssueDesc
                        ? "border-amber-300/60 bg-amber-50/40 dark:bg-amber-950/20"
                        : "border-border bg-muted/10",
                    )}
                  >
                    <div className="flex gap-2">
                      <MediaPreview item={item} className="h-16 w-16 shrink-0" />
                      <div className="min-w-0 flex-1 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="truncate text-[10px] text-muted-foreground">{item.file.name}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 shrink-0"
                            onClick={() => removeItem(item.id, phase)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                        {roomOptions.length > 0 ? (
                          <Select
                            value={item.roomName || undefined}
                            onValueChange={(value) => updateItem(item.id, phase, { roomName: value })}
                          >
                            <SelectTrigger className="h-7 text-xs">
                              <SelectValue placeholder="Room (optional)" />
                            </SelectTrigger>
                            <SelectContent>
                              {roomOptions.map((name) => (
                                <SelectItem key={name} value={name}>
                                  {name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            className="h-7 text-xs"
                            placeholder="Room (optional)"
                            value={item.roomName}
                            onChange={(e) => updateItem(item.id, phase, { roomName: e.target.value })}
                          />
                        )}
                        <label className="flex cursor-pointer items-center gap-1.5 text-xs">
                          <Checkbox
                            checked={item.isIssue}
                            onCheckedChange={(v) =>
                              updateItem(item.id, phase, {
                                isIssue: v === true,
                                issueDescription: v === true ? item.issueDescription : "",
                              })
                            }
                          />
                          Issue
                        </label>
                      </div>
                    </div>
                    {item.isIssue && (
                      <Textarea
                        placeholder="Describe the issue…"
                        value={item.issueDescription}
                        onChange={(e) => updateItem(item.id, phase, { issueDescription: e.target.value })}
                        rows={2}
                        className="resize-none text-xs"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {roomOptions.length > 0 && (
          <div className="flex flex-wrap items-end gap-2 rounded-lg border border-dashed p-3">
            <div className="min-w-[140px] flex-1 space-y-1">
              <Label className="text-xs text-muted-foreground">Room not listed?</Label>
              <Input
                className="h-8 text-xs"
                placeholder="Custom room name"
                value={customRoom}
                onChange={(e) => setCustomRoom(e.target.value)}
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!customRoom.trim() || roomOptions.includes(customRoom.trim())}
              onClick={() => {
                const name = customRoom.trim();
                if (!name || roomOptions.includes(name)) return;
                setExtraRooms((prev) => [...prev, name]);
                setCustomRoom("");
              }}
            >
              Add room
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Import turnover photos
          </DialogTitle>
          <DialogDescription>
            Upload all before photos, then all after photos. Room assignment is optional — you can
            import first and organize by room later if needed.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          {STEPS.map((s, i) => (
            <span key={s} className="flex items-center gap-1">
              {i > 0 && <ChevronRight className="h-3 w-3" />}
              <button
                type="button"
                disabled={s === "review" && step !== "review"}
                onClick={() => {
                  if (s === "review") return;
                  if (s === "before" || (s === "after" && step !== "before")) setStep(s);
                }}
                className={cn(
                  "rounded px-2 py-0.5 transition-colors",
                  step === s ? "bg-primary/10 font-medium text-primary" : "hover:bg-muted",
                  s === "review" && step !== "review" && "cursor-default opacity-60",
                )}
              >
                {stepLabel(s)}
                {s === "before" && beforeItems.length > 0 && ` (${beforeItems.length})`}
                {s === "after" && afterItems.length > 0 && ` (${afterItems.length})`}
              </button>
            </span>
          ))}
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto py-1 pr-1">
          {existingSubmissionId && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>Photos will be added to the existing report for this task.</AlertDescription>
            </Alert>
          )}

          {!checklistId && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                No checklist on this task. Assign by room name, or use Edit Task to attach a checklist.
              </AlertDescription>
            </Alert>
          )}

          {step === "before" && renderPhasePanel("before")}
          {step === "after" && renderPhasePanel("after")}

          {step === "review" && (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg border p-3">
                  <p className="text-sm font-medium">Before</p>
                  <p className="text-2xl font-semibold">{beforeItems.length}</p>
                  <p className="text-xs text-muted-foreground">
                    {beforeItems.filter((p) => p.isIssue).length} flagged as issues
                  </p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="text-sm font-medium">After</p>
                  <p className="text-2xl font-semibold">{afterItems.length}</p>
                  <p className="text-xs text-muted-foreground">
                    {afterItems.filter((p) => p.isIssue).length} flagged as issues
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="import-notes">Notes (optional)</Label>
                <Textarea
                  id="import-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="resize-none"
                />
              </div>

              {taskStatus !== "completed" && (
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="mark-complete"
                    checked={markTaskCompleted}
                    onCheckedChange={(v) => setMarkTaskCompleted(v === true)}
                  />
                  <div className="space-y-0.5">
                    <Label htmlFor="mark-complete" className="cursor-pointer font-normal">
                      Mark task completed
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Sets checklist complete on the schedule. Inventory status is unchanged.
                    </p>
                  </div>
                </div>
              )}

              <p className="text-xs text-muted-foreground">
                Your progress is saved if you close this dialog. Use Back to edit before or after photos.
              </p>
            </div>
          )}
        </div>

        {importPhotos.isPending && uploadProgress && uploadProgress.total > 0 && (
          <div className="space-y-2 rounded-lg border bg-muted/30 p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {uploadProgress.phase === "uploading" ? "Uploading files…" : "Saving to report…"}
              </span>
              <span className="font-medium tabular-nums">
                {uploadProgress.completed} / {uploadProgress.total}
              </span>
            </div>
            <Progress
              value={Math.round((uploadProgress.completed / uploadProgress.total) * 100)}
              className="h-2"
            />
            <p className="text-xs text-muted-foreground">
              {uploadProgress.phase === "uploading"
                ? "Uploading several files at once — large batches can take a few minutes."
                : "Almost done — writing photo records."}
            </p>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          {step !== "before" && (
            <Button type="button" variant="ghost" className="mr-auto" onClick={goBack} disabled={importPhotos.isPending}>
              <ChevronLeft className="mr-1 h-4 w-4" />
              Back
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={importPhotos.isPending}>
            Cancel
          </Button>
          {step === "review" ? (
            <Button onClick={handleSubmit} disabled={importPhotos.isPending || !canSubmit}>
              {importPhotos.isPending && uploadProgress ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {uploadProgress.phase === "uploading"
                    ? `Uploading ${uploadProgress.completed}/${uploadProgress.total}…`
                    : `Saving ${uploadProgress.completed}/${uploadProgress.total}…`}
                </>
              ) : (
                <>Import {totalCount} file{totalCount !== 1 ? "s" : ""}</>
              )}
            </Button>
          ) : (
            <Button onClick={goNext}>
              {step === "before" ? "Continue to after photos" : "Review & import"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
